import {skipToken} from '@reduxjs/toolkit/query';
import {Card, showNotification, Switch} from 'platform/components';
import {Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, isNotNil} from 'ramda';

import {useGetUserQuery} from '@dms/api/customer';
import {
  TelephonyLine,
  useApproveTelephonyLineRecordingConsentMutation,
  useRevokeTelephonyLineRecordingConsentMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {getUserName, handleApiError} from '@dms/shared';

import {Nullish, parseDate, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface TelephonyLineRecordingConsentCardProps extends RequiredTestIdProps {
  line: TelephonyLine | Nullish;
  isReadOnly: boolean;
}

export function TelephonyLineRecordingConsentCard(props: TelephonyLineRecordingConsentCardProps) {
  const formatDateTime = useDateTimeFormatter();

  const [approveConsent, {isLoading: isApproving}] =
    useApproveTelephonyLineRecordingConsentMutation();
  const [revokeConsent, {isLoading: isRevoking}] = useRevokeTelephonyLineRecordingConsentMutation();

  const consent = props.line?.recordingConsent;

  const {data: approver} = useGetUserQuery(
    isNotNil(consent?.approvedBy) ? {id: consent.approvedBy} : skipToken
  );

  const line = props.line;

  if (isNil(line) || isNil(consent)) {
    return null;
  }

  const handleChange = (isApproved: boolean) => {
    const mutation = isApproved ? approveConsent : revokeConsent;

    mutation({lineId: line.id})
      .unwrap()
      .then(() =>
        showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'))
      )
      .catch(handleApiError);
  };

  const approvedAt = parseDate(consent.approvedAt);
  const approvedLabel = [
    isNotNil(approvedAt)
      ? i18n.t('page.settings.telephony.recordingApprovedAt', {
          date: formatDateTime('dateTimeShort', approvedAt),
        })
      : null,
    getUserName(approver),
  ]
    .filter(isNotNil)
    .join(' · ');

  return (
    <Card
      title={i18n.t('page.settings.telephony.recordingCardTitle')}
      data-testid={suffixTestId('card', props)}
    >
      <VStack spacing={4}>
        <Text color="secondary" size="small">
          {i18n.t('page.settings.telephony.recordingDescription')}
        </Text>
        <Switch
          value={consent.approved}
          onChange={handleChange}
          label={i18n.t('page.settings.telephony.recordingSwitch')}
          isDisabled={props.isReadOnly}
          isLoading={isApproving || isRevoking}
          data-testid={suffixTestId('switch', props)}
        />
        <Show when={consent.approved}>
          <Text color="secondary" size="xSmall" data-testid={suffixTestId('approvedAt', props)}>
            {approvedLabel}
          </Text>
        </Show>
      </VStack>
    </Card>
  );
}
